import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryIcon from '@mui/icons-material/History';
import StatusChip from '../common/StatusChip';
import { formatDate } from '../../utils/dateUtils';
import { getDisplayStatus } from '../../utils/enquiryDisplayUtils';
import { STATUS_COLORS } from '../../config/statusConfig';

/**
 * Status → left border accent color (hex) for card
 */
const STATUS_ACCENT = {
  default: '#e5e7eb',
  primary: '#93c5fd',
  secondary: '#c4b5fd',
  success: '#86efac',
  warning: '#fcd34d',
  error: '#fca5a5',
  info: '#93c5fd',
};

const getStatusAccent = (status) => {
  const variant = STATUS_COLORS[status] || 'default';
  return STATUS_ACCENT[variant] || STATUS_ACCENT.default;
};

/**
 * Single enquiry card for mobile list view.
 * Same data shape as table row (transformed enquiry).
 */
const EnquiryCardMobile = ({
  enquiry,
  onEnquiryNumberClick,
  onActionClick,
  onHistoryClick,
  showAssignedTo = false,
}) => {
  const displayStatus = getDisplayStatus(enquiry);
  const accent = getStatusAccent(displayStatus.label || enquiry?.status);
  const requirements = enquiry?.requirements || enquiry?.requirement || [];
  const itemCount = requirements.length;
  const requirementSummary =
    enquiry?.requirementsSummary ||
    (requirements.length > 0
      ? requirements.map((r) => `${r.item_name || 'Item'} (${r.item_quantity || 0})`).join(', ')
      : '—');

  const handleNumberClick = (e) => {
    e.stopPropagation();
    if (onEnquiryNumberClick && enquiry) onEnquiryNumberClick(enquiry, 'enquiryNumber', e);
  };

  const handleActionClick = (e) => {
    e.stopPropagation();
    if (onActionClick) onActionClick(e, enquiry);
  };

  const handleHistoryClick = (e) => {
    e.stopPropagation();
    if (onHistoryClick) onHistoryClick(e, enquiry);
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        backgroundColor: 'background.paper',
        overflow: 'hidden',
        borderLeft: '4px solid',
        borderLeftColor: accent,
        transition: 'box-shadow 0.2s ease',
        '&:active': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        },
      }}
    >
      {/* Top row: Enquiry Number (clickable) + Status */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.5,
          pt: 1.25,
          pb: 0.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          onClick={handleNumberClick}
        >
          {enquiry?.enquiryNumber || '—'}
        </Typography>
        <StatusChip status={displayStatus.label || enquiry?.status} />
      </Box>

      {/* Second row: Client Name, City, State */}
      <Box sx={{ px: 1.5, py: 0.25 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {enquiry?.clientName || '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {enquiry?.locationDisplay || [enquiry?.city, enquiry?.state].filter(Boolean).join(', ') || '—'}
        </Typography>
      </Box>

      {/* Third row: Product / Requirement summary + item count */}
      <Box sx={{ px: 1.5, py: 0.25 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.75rem',
          }}
        >
          {enquiry?.mainProductType || requirementSummary}
          {itemCount > 0 && ` · ${itemCount} item${itemCount !== 1 ? 's' : ''}`}
        </Typography>
      </Box>

      {/* Fourth row: Enq Owner, Next Follow-up Date */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          px: 1.5,
          py: 0.5,
        }}
      >
        {showAssignedTo && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Enq Owner: {enquiry?.assignedTo || '—'}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.75rem',
            color: enquiry?.next_follow_up_on
              ? (displayStatus.isMissed ? 'error.main' : 'text.secondary')
              : 'text.secondary',
            fontStyle: enquiry?.next_follow_up_on ? 'normal' : 'italic',
          }}
        >
          Next follow-up: {enquiry?.next_follow_up_on ? formatDate(enquiry.next_follow_up_on) : 'No Follow Up Date Added'}
        </Typography>
      </Box>

      {/* Bottom row: Last updated + History icon + Three-dot menu */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 0.5,
          pb: 0.75,
          pt: 0.25,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', pl: 1 }}>
          Updated: {formatDate(enquiry?.lastUpdatedOn)}
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0 }}>
          {onHistoryClick && (
            <IconButton size="small" onClick={handleHistoryClick} aria-label="View history" sx={{ p: 0.5 }}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" onClick={handleActionClick} aria-label="Actions" sx={{ p: 0.5 }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default EnquiryCardMobile;
